<template>
  <div
    class="main-image-and-alts-container"
    :class="aspectRatioClass"
  >
    <AltImageSelector
      v-if="!isMobile && hasCarouselImages"
      class="vertical-alternate"
      aria-label="alternate images"
      :orientation="orientation"
      :carousel-images="carouselImages"
      :current-page="currentPage"
      :current-page-on-hover="currentPageOnHover"
      :seo-alt="seoAlt"
      @image-change="updateCurrentPage"
      @image-change-on-hover="updateCurrentPageOnHover"
    />

    <MainImageContainer
      :current-page="currentPage"
      :current-page-on-hover="currentPageOnHover"
      :show-indicator="isMobile"
      :first-alt-dot="firstAltDot"
      :has-carousel-images="hasCarouselImages"
      @update-first-alt-dot="updateFirstAltDot"
      @update-carousel="updateCarousel"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { getSeoAlt } from '@utils/getSeoAlt';
import { useConsolidatedProductDetails } from '@composables/useConsolidatedProductDetails';
import { useCarouselSelectedImage } from '@composables/useCarouselSelectedImage';
import { useImages } from '@composables/useImages';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useBrand } from '@composables/useBrand';
import { useTopLevelCategoryFlags } from '@modules/featureControl';
import { ALT_IMAGE_ORIENTATION } from '../../../constants/common';

import MainImageContainer from './MainImageContainer';
import AltImageSelector from './AltImageSelector';

const { productName, brandName } = useCommonProductGetters();
const { productName: consolidatedName } = useConsolidatedProductDetails();
const { isBcom } = useBrand();
const seoAlt = computed(() => getSeoAlt(consolidatedName.value || productName.value, brandName.value));
const { carouselImages } = useImages();
const hasCarouselImages = computed(() => carouselImages.value?.length > 1);
const { isMobile, isTablet } = useDeviceType();
const orientation = computed(() => (isTablet.value ? ALT_IMAGE_ORIENTATION.horizontal : ALT_IMAGE_ORIENTATION.vertical));
const { isHomeCategory } = useTopLevelCategoryFlags();
const aspectRatioClass = computed(() => ({ square: isBcom && isHomeCategory }));
// TODO: instead of props drilling for currentPage and firstAltDot use provide instead
const {
  currentPage,
  currentPageOnHover,
  updateCurrentPage,
  updateCurrentPageOnHover,
  updateCarousel,
  firstAltDot,
  updateFirstAltDot,
} = useCarouselSelectedImage();
</script>

<style lang="scss" scoped>
  @import "~settingSCSS";
  .main-image-and-alts-container {
    display: flex;
    width: 100%;
    overflow: hidden;
    touch-action: pan-y;
    @include position-sticky(10);
    flex-direction: column-reverse;

    @include breakpoint(large) {
      flex-direction: row;
    }

  &.square {
    :deep() {
      .picture-image {
        aspect-ratio: 1/1;
        object-fit: contain;
        @include breakpoint(medium up) {
          height: 100%;
        }
      }
    }
  }

    :deep() {
      .mobile-entries-cta {
        .product-images-cta-container {
          @include breakpoint(small only) {
            position: static;
            display: flex;
            justify-content: center;
          }
        }
      }
      .picture-container {
        width: 100%;
        height: auto;
      }
      .picture-image {
        aspect-ratio: 4/5;
        object-fit: cover;
        @include breakpoint(medium up) {
          height: 100%;
        }
      }
    }
  }

  .vertical-alternate {
    min-width: 100%;
    width: 100%;
    display: none;

    @include breakpoint(medium up) {
      display: block;
      min-width: 106px;
      width: 106px;
    }
    @include breakpoint(large down) {
      min-width: 68px;
      width: 68px;
    }
    @include breakpoint(medium down) {
      min-width: 100%;
      width: 100%;
    }
    :deep() {
      .picture-container {
        width: 100%;
        height: auto;

        .picture-image {
          height: 100%;
          object-fit: contain;
        }
      }
    }
  }
</style>
